/**
 * Storage for the Agenda Builder. Two persistence layers:
 *
 *   1. IndexedDB (`__agendaDatabookBlobs`) — holds the raw xlsx bytes for the
 *      project's pinned databook. Bytes live here (not localStorage) because
 *      databooks are a few MB each and would blow the localStorage 5–10 MB
 *      quota almost immediately.
 *
 *   2. localStorage (`__agendaDatabookPin`) — per-project pin metadata
 *      (source, fileName, detected question columns, confidence). Small and
 *      cheap to read on every render.
 *
 * The pair is kept in sync by the helpers below — `clearAgendaDatabookPin`
 * wipes both, `saveAgendaDatabookPin` writes only the metadata, and the
 * blob is stored separately so detection can re-run without re-uploading.
 */

import { apiFetch } from "../../../api/fddClient";

const DB_NAME = "irl-agenda";
const DB_VERSION = 1;
const STORE_NAME = "databook-blobs";

const PIN_KEY = "__agendaDatabookPin";
const INPUTS_KEY = "__agendaInputs";

export type AgendaPinSource = "builder" | "manual";
export type AgendaDetectionConfidence = "exact" | "heuristic" | "user" | "unresolved";

export type AgendaDatabookPin = {
  source: AgendaPinSource;
  fileName: string;
  pinnedAt: string; // ISO
  fileSize?: number;
  /**
   * Resolved 1-based column number for the Questions column on each
   * recognized tab. `null` means detection failed and the UI should ask
   * the user to pick.
   */
  questionColByTab: Record<string, number | null>;
  /**
   * 1-based column number for the Agenda routing column on each tab.
   * Absent on older pins (pre-routing-columns build) — fall back to
   * including all questions when missing or all-null.
   */
  agendaColByTab?: Record<string, number | null>;
  /**
   * 1-based column number for the IRL routing column on each tab.
   * Absent on older pins.
   */
  irlColByTab?: Record<string, number | null>;
  /**
   * 1-based row number for the header row on each tab — captured during
   * detection so extraction doesn't have to re-scan to find it.
   */
  headerRowByTab: Record<string, number>;
  /**
   * Actual sheet name in the workbook that matched the canonical tab.
   * Stored alongside so the server-side hierarchy walker opens the right
   * sheet even when the workbook used a non-canonical name.
   */
  sheetNameByTab: Record<string, string>;
  detectionConfidence: AgendaDetectionConfidence;
};

type PinMap = Record<string, AgendaDatabookPin>;

export type AgendaSector = "midstream" | "renewables" | "solar-wind" | "fiber" | "more";

/** User-provided inputs that drive bracketed placeholder substitution. */
export type AgendaInputs = {
  sector: AgendaSector;
  fiscalPeriod: string;
  entities: string[];
  parentEntity: string;
  relatedParties: string[];
};

type InputsMap = Record<string, AgendaInputs>;

export const DEFAULT_AGENDA_INPUTS: AgendaInputs = {
  sector: "midstream",
  fiscalPeriod: "",
  entities: [],
  parentEntity: "",
  relatedParties: [],
};

/* ────────────────────────── IndexedDB helpers ────────────────────────── */

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const req = fn(store);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}

export async function stashDatabookBlob(projectId: string, bytes: ArrayBuffer): Promise<void> {
  // Kept as a no-op for compatibility with the existing auto-pin call site.
  // The canonical store now owns the bytes — written server-side after a
  // successful databook build. We keep the IndexedDB write path only to
  // back the legacy "manual upload to agenda" flow.
  await withStore("readwrite", (s) => s.put(bytes, projectId));
}

/**
 * Load the databook bytes for a project. Prefers the canonical store
 * (`/api/databook-store/:projectId/databook`) so the agenda and the
 * commentary feature always see the same source of truth. Falls back to
 * IndexedDB for projects whose databook was manually pinned (legacy path).
 */
export async function loadDatabookBlob(projectId: string): Promise<ArrayBuffer | null> {
  // Try the canonical store first.
  try {
    const res = await apiFetch(`/api/databook-store/${encodeURIComponent(projectId)}/databook`);
    if (res.ok) {
      const buf = await res.arrayBuffer();
      if (buf.byteLength > 0) return buf;
    }
  } catch {
    // Fall through to IndexedDB.
  }
  const result = await withStore<ArrayBuffer | undefined>("readonly", (s) => s.get(projectId));
  return result ?? null;
}

export async function clearDatabookBlob(projectId: string): Promise<void> {
  await withStore("readwrite", (s) => s.delete(projectId));
}

/* ────────────────────────── Pin metadata helpers ────────────────────── */

function readPinMap(): PinMap {
  try {
    const raw = localStorage.getItem(PIN_KEY);
    return raw ? (JSON.parse(raw) as PinMap) : {};
  } catch {
    return {};
  }
}

function writePinMap(map: PinMap): void {
  try {
    localStorage.setItem(PIN_KEY, JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
}

export function getAgendaDatabookPin(projectId: string): AgendaDatabookPin | null {
  return readPinMap()[projectId] ?? null;
}

export function saveAgendaDatabookPin(projectId: string, pin: AgendaDatabookPin): void {
  const map = readPinMap();
  map[projectId] = pin;
  writePinMap(map);
}

/* ────────────────────────── Inputs helpers ─────────────────────────── */

function readInputsMap(): InputsMap {
  try {
    const raw = localStorage.getItem(INPUTS_KEY);
    return raw ? (JSON.parse(raw) as InputsMap) : {};
  } catch {
    return {};
  }
}

function writeInputsMap(map: InputsMap): void {
  try {
    localStorage.setItem(INPUTS_KEY, JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
}

export function getAgendaInputs(projectId: string): AgendaInputs {
  const stored = readInputsMap()[projectId];
  return stored ? { ...DEFAULT_AGENDA_INPUTS, ...stored } : { ...DEFAULT_AGENDA_INPUTS };
}

export function saveAgendaInputs(projectId: string, inputs: AgendaInputs): void {
  const map = readInputsMap();
  map[projectId] = inputs;
  writeInputsMap(map);
}

/**
 * Remove both the pin metadata and the IndexedDB blob in one go.
 */
export async function clearAgendaDatabookPin(projectId: string): Promise<void> {
  const map = readPinMap();
  if (map[projectId]) {
    delete map[projectId];
    writePinMap(map);
  }
  await clearDatabookBlob(projectId).catch(() => {
    // Best-effort — pin metadata cleared either way.
  });
}
